import React from "react";
import Preloader from "../helper/Preloader";
import ColorInit from "../helper/ColorInit";
import HeaderThree from "../components/HeaderThree";
import Breadcrumb from "../components/Breadcrumb";
import Footer from "../components/Footer";
import ScrollToTop from "react-scroll-to-top";
import NewsletterTwo from "../components/NewsletterTwo";

const AboutPage = () => {
    return (
        <>
            <ColorInit color={true} />
            <ScrollToTop smooth color="var(--brand-primary)" />
            <Preloader />
            <HeaderThree />
            <Breadcrumb title={"About Us"} />

            {/* Welcome & Our Story Section */}
            <section className="about-section py-80 bg-white">
                <div className="container">
                    <div className="row gy-5 align-items-center">
                        <div className="col-lg-12 text-center mb-40">
                            <h2 className="mb-24 fw-bold text-gray-900 leading-tight">Welcome to Vedha’s Culture</h2>
                            <p className="text-gray-600 text-lg max-w-800 mx-auto leading-relaxed">
                                A creative space where art, handmade crafts, and happiness come together.
                                From beautiful yarn bags to creative workshops, we believe in turning simple ideas into handmade memories.
                            </p>
                        </div>
                    </div>

                    <div className="row justify-content-center gy-4">
                        {/* Our Story - Top Full Width */}
                        <div className="col-lg-10 mb-20">
                            <div className="p-40 bg-main-50 rounded-24 border border-main-100 shadow-sm transition-3 hover-shadow-md text-center">
                                <div className="w-64 h-64 bg-main-600 text-white rounded-circle flex-center mb-24 shadow-sm mx-auto">
                                    <i className="ph-fill ph-book-open-text text-3xl"></i>
                                </div>
                                <h4 className="mb-20 fw-bold">Our Story</h4>
                                <p className="text-gray-700 leading-relaxed text-lg max-w-800 mx-auto">
                                    Vedha’s Culture started with a passion for creativity and handmade artistry.
                                    What began as a small dream has now grown into a beautiful community of learners, creators, and craft lovers.
                                </p>
                            </div>
                        </div>

                        {/* Mission & Vision - Side by Side */}
                        <div className="col-lg-5">
                            <div className="p-40 bg-success-50 rounded-24 border border-success-100 h-100 shadow-sm transition-3 hover-shadow-md">
                                <div className="w-60 h-60 bg-success-600 text-white rounded-circle flex-center mb-24 shadow-sm">
                                    <i className="ph-fill ph-target text-3xl"></i>
                                </div>
                                <h4 className="mb-16 fw-bold">Our Mission</h4>
                                <p className="text-gray-700 leading-relaxed">
                                    Our mission is to inspire creativity in every age group and help people learn
                                    beautiful handmade skills with confidence, happiness, and creativity.
                                </p>
                            </div>
                        </div>
                        <div className="col-lg-5">
                            <div className="p-40 bg-info-50 rounded-24 border border-info-100 h-100 shadow-sm transition-3 hover-shadow-md">
                                <div className="w-60 h-60 bg-info-600 text-white rounded-circle flex-center mb-24 shadow-sm">
                                    <i className="ph-fill ph-eye text-3xl"></i>
                                </div>
                                <h4 className="mb-16 fw-bold">Our Vision</h4>
                                <p className="text-gray-700 leading-relaxed">
                                    To become a globally recognized creative community that empowers individuals to discover their inner artist,
                                    fostering a world where handcrafted beauty and artistic expression bring joy to every home.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Founder Note Section */}
            <section className="founder-section py-80 bg-neutral-50 overflow-hidden">
                <div className="container">
                    <div className="row gy-5 align-items-center">
                        <div className="col-lg-5">
                            <div className="founder-image position-relative">
                                <div className="bg-white rounded-24 shadow-lg overflow-hidden border border-gray-100">
                                    <img
                                        src="/assets/images/founder/founder.jpeg"
                                        alt="Founder Dharani"
                                        className="w-100 rounded-16 shadow-inner object-fit-cover"
                                        style={{ height: '500px' }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-7">
                            <div className="ps-lg-40">
                                <div className="mb-20 d-flex align-items-center gap-12 text-main-600 fw-bold text-sm uppercase tracking-widest">
                                    <div className="w-40 h-1 bg-main-600"></div>
                                    Founder’s Note
                                </div>
                                <h2 className="mb-32 fw-bold text-gray-900">A Journey of Passion & Creativity</h2>
                                <div className="founder-note-content space-y-24">
                                    <p className="text-gray-700 text-lg italic leading-relaxed">
                                        "Vedha’s Culture was created from passion, creativity, and the inspiration of my son, Vedarsh — the heart behind our name."
                                    </p>
                                    <p className="text-gray-600 leading-relaxed">
                                        While our family has been rooted in the sweet shop business for over a decade,
                                        I wanted to build a space that reflected elegance through handmade artistry and creative expression.
                                    </p>
                                    <p className="text-gray-600 leading-relaxed">
                                        What began as a personal passion has now grown into a brand that celebrates craftsmanship,
                                        creativity, and timeless handmade experiences.
                                    </p>
                                    <div className="mt-40">
                                        <p className="mb-8 font-bold text-gray-900 text-xl">With love,</p>
                                        <p className="fw-bold text-main-600 text-2xl font-signature">Dharani</p>
                                        <p className="text-sm font-semibold opacity-90 uppercase tracking-wider">Founder of Vedha’s Culture</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What We Do Section */}
            <section className="what-we-do py-100 bg-white">
                <div className="container">
                    <div className="section-heading text-center mb-60">
                        <span className="text-main-600 fw-bold text-sm uppercase tracking-widest mb-12 d-block">Our Expertise</span>
                        <h2 className="mb-16 fw-bold text-gray-900">Creative Offerings</h2>
                        <p className="text-gray-500 max-w-600 mx-auto text-lg">
                            We specialize in a variety of handmade arts and professional workshops,
                            blending tradition with modern creativity.
                        </p>
                    </div>

                    <div className="row g-4 justify-content-center">
                        {[
                            { title: 'Chunky Yarn Handbags', icon: 'ph-handbag', bg: 'bg-main-50', color: 'text-main-600' },
                            { title: 'Canvas Mesh Sheet Bags', icon: 'ph-shopping-bag', bg: 'bg-info-50', color: 'text-info-600' },
                            { title: 'Chenille Yarn Bags', icon: 'ph-shopping-bag', bg: 'bg-main-two-50', color: 'text-main-two-600' },
                            { title: 'Pipe Cleaner Flower Art', icon: 'ph-flower-lotus', bg: 'bg-danger-50', color: 'text-danger-600' },
                            { title: 'Needle Felt Art', icon: 'ph-needle', bg: 'bg-success-50', color: 'text-success-600' },
                            { title: 'Quick Knit Yarn Toys', icon: 'ph-smiley', bg: 'bg-warning-50', color: 'text-warning-600' },
                            { title: 'Cap Knitting (Centro)', icon: 'ph-hoodie', bg: 'bg-warning-50', color: 'text-warning-600' },
                            { title: 'Bobo Balloon Workshops', icon: 'ph-balloon', bg: 'bg-danger-50', color: 'text-danger-600' },
                            { title: 'DIY Painting Kits', icon: 'ph-palette', bg: 'bg-info-50', color: 'text-info-600' },
                            { title: 'Pipe Cleaner Toys', icon: 'ph-magic-wand', bg: 'bg-main-two-50', color: 'text-main-two-600' },
                        ].map((item, index) => (
                            <div key={index} className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
                                <div className="p-24 bg-white border border-gray-100 rounded-28 h-100 transition-5 hover-shadow-xl hover-translate-y-[-8px] text-center group">
                                    <div className={`w-80 h-80 mx-auto ${item.bg} ${item.color} rounded-circle flex-center mb-20 transition-5 group-hover-scale-110 shadow-sm`}>
                                        <i className={`ph-fill ${item.icon} text-4xl`}></i>
                                    </div>
                                    <h6 className="text-sm font-bold text-gray-800 leading-tight group-hover-text-main-600 transition-3">{item.title}</h6>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            {/* Why People Love Us Section */}
            <section className="features-section py-80 bg-neutral-50">
                <div className="container">
                    <div className="section-heading text-center mb-60">
                        <h2 className="mb-16 fw-bold">Why People Love Vedha’s Culture</h2>
                    </div>
                    <div className="row gy-4">
                        {[
                            { title: 'Easy-to-learn classes', desc: 'Step-by-step guidance for every skill level.', icon: 'ph-graduation-cap', color: 'info' },
                            { title: 'Friendly teaching support', desc: 'Always here to help you perfect your craft.', icon: 'ph-users-three', color: 'main-two' },
                            { title: 'Trendy handmade creations', desc: 'Modern designs that stay ahead of the curve.', icon: 'ph-trend-up', color: 'main' },
                            { title: 'Suitable for beginners', desc: 'No prior experience needed to start creating.', icon: 'ph-smiley', color: 'success' },
                            { title: 'Creative and joyful experience', desc: 'A stress-free environment to relax and learn.', icon: 'ph-heart', color: 'danger' },
                        ].map((feature, index) => (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="p-24 bg-white rounded-24 shadow-sm border border-gray-100 h-100 transition-3 hover-translate-y-[-5px]">
                                    <div className="d-flex align-items-center gap-16 mb-16">
                                        <div className={`w-48 h-48 bg-${feature.color}-50 text-${feature.color}-600 rounded-12 flex-center shrink-0`}>
                                            <i className={`ph-fill ${feature.icon} text-2xl`}></i>
                                        </div>
                                        <h6 className="mb-0 fw-bold text-gray-900">{feature.title}</h6>
                                    </div>
                                    <p className="text-gray-500 text-sm leading-relaxed">{feature.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            {/* NewsletterTwo */}
      <NewsletterTwo />
            <Footer />
        </>
    );
};

export default AboutPage;